import { useCallback } from "react";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  $api,
  fetchClient,
  invalidate,
  isNotFound,
  orderingParam,
} from "./client";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type PlanningRun = Schemas["PlanningRun"];
export type PlannedOrder = Schemas["PlannedOrder"];
export type PlannedOrderStatus = PlannedOrder["status"];
export type NodeShortages = Schemas["NodeShortages"];
export type Peg = Schemas["Peg"];
export type Need = Schemas["Need"];

export function useStartPlanningRun() {
  return useMutation({
    mutationFn: async (carryOver: boolean) =>
      (
        await fetchClient.POST("/api/v1/planning-runs/", {
          body: { carryOver },
        })
      ).data!,
  });
}

export const usePlanningRun = (id: number) =>
  $api.useQuery("get", "/api/v1/planning-runs/{id}/", {
    params: { path: { id } },
  });

/** Every node whose Full plan runs short. Computed on request: slow. */
export const useShortages = (id: number) =>
  $api.useQuery(
    "get",
    "/api/v1/planning-runs/{id}/shortages/",
    { params: { path: { id } } },
    { staleTime: 5 * 60 * 1000 },
  );

/** Polls a run's calculation until it succeeds or fails. */
export function usePlanningRunStatus(run: PlanningRun | null) {
  return useQuery({
    // Keyed by task so a second calculation never reads the first one's result.
    queryKey: ["planning-run-status", run?.taskId],
    enabled: run !== null,
    queryFn: async () =>
      (
        await fetchClient.GET("/api/v1/planning-runs/{id}/status/", {
          params: { path: { id: run!.id } },
        })
      ).data!,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === "succeeded" || state === "failed" ? false : 750;
    },
  });
}

export const useListPlanningRuns = ({
  ordering,
  ...query
}: {
  ordering?: string;
  limit: number;
  offset: number;
}) =>
  $api.useQuery(
    "get",
    "/api/v1/planning-runs/",
    { params: { query: { ...query, ordering: orderingParam(ordering) } } },
    { placeholderData: keepPreviousData },
  );

export function useDeleteRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchClient.DELETE("/api/v1/planning-runs/{id}/", {
        params: { path: { id } },
      }),
    onSuccess: () => invalidate(queryClient, "/api/v1/planning-runs/"),
  });
}

export const usePlannedOrders = (
  {
    run,
    ...query
  }: {
    run: number;
    limit: number;
    offset: number;
    item: number[];
    location: number[];
    supplier: number[];
    flavor: PlannedOrder["flavor"][];
    status: PlannedOrderStatus[];
    // Linked to an open order, or a new one; both when absent.
    existing?: boolean;
  },
  { enabled = true } = {},
) =>
  $api.useQuery(
    "get",
    "/api/v1/planning-runs/{run}/orders/",
    { params: { path: { run }, query } },
    { enabled, placeholderData: keepPreviousData },
  );

/** A node's orders in a run. */
export function useNodeOrders(
  run: number,
  item: number,
  location: number,
  { enabled = true } = {},
) {
  // ponytail: one page; a node rarely has more than 200 orders.
  return usePlannedOrders(
    {
      run,
      limit: 200,
      offset: 0,
      item: [item],
      location: [location],
      supplier: [],
      flavor: [],
      status: [],
    },
    { enabled },
  );
}

// A selected order disappears when a recalculation replaces it: that 404 is
// an empty selection, not an error. Its pegging queries do the same.
const notFoundIsEmpty = {
  throwOnError: (error: Error) => !isNotFound(error),
  retry: (count: number, error: Error) => !isNotFound(error) && count < 2,
};

export const usePlannedOrder = (run: number, id: number | null) =>
  $api.useQuery(
    "get",
    "/api/v1/planning-runs/{run}/orders/{id}/",
    { params: { path: { run, id: id ?? 0 } } },
    { enabled: id !== null, ...notFoundIsEmpty },
  );

/** The orders a planned order is needed for, followed upward. */
export const useNeededFor = (
  run: number,
  id: number,
  { enabled = true } = {},
) =>
  $api.useQuery(
    "get",
    "/api/v1/planning-runs/{run}/orders/{id}/needed-for/",
    { params: { path: { run, id } } },
    { enabled, ...notFoundIsEmpty },
  );

/** The orders supplying a planned order's draws, followed downward. */
export const useNeeds = (run: number, id: number) =>
  $api.useQuery(
    "get",
    "/api/v1/planning-runs/{run}/orders/{id}/needs/",
    { params: { path: { run, id } } },
    notFoundIsEmpty,
  );

/** Decisions and recalculations reshape the run, its orders and its charts. */
export function usePlanInvalidation() {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      void invalidate(
        queryClient,
        "/api/v1/planning-runs/",
        "/api/v1/stock/projection/",
      ),
    [queryClient],
  );
}

export function useRecalculate(id: number) {
  return useMutation({
    mutationFn: async () =>
      (
        await fetchClient.POST("/api/v1/planning-runs/{id}/recalculate/", {
          params: { path: { id } },
        })
      ).data!,
  });
}

export function useUpdatePlannedOrder() {
  const invalidatePlan = usePlanInvalidation();
  return useMutation({
    mutationFn: async ({
      run,
      id,
      ...body
    }: { run: number; id: number } & Schemas["PatchedPlannedOrderRequest"]) =>
      (
        await fetchClient.PATCH("/api/v1/planning-runs/{run}/orders/{id}/", {
          params: { path: { run, id } },
          body,
        })
      ).data!,
    onSuccess: invalidatePlan,
  });
}
